/**
 * Feedback Redux Hooks
 * Custom hooks for managing inline feedback form state using Redux
 */
import { useSelector, useDispatch } from 'react-redux';
import { useCallback } from 'react';
import type { RootState, AppDispatch } from '../store';
import type { FeedbackType } from '../types/feedback';
import {
  startFeedback as startFeedbackAction,
  setFeedbackText as setFeedbackTextAction,
  startSubmitting as startSubmittingAction,
  submitFeedbackSuccess as submitFeedbackSuccessAction,
  submitFeedbackError as submitFeedbackErrorAction,
  resetFeedback as resetFeedbackAction,
  selectIsActive,
  selectFeedbackType,
  selectMessageId,
  selectShowForm,
  selectFeedbackText,
  selectIsSubmitted,
  selectIsSubmitting,
  selectFeedbackChatId,
} from '../store/slices/feedbackSlice';

export const useFeedback = () => {
  const dispatch = useDispatch<AppDispatch>();

  // Selectors
  const isActive = useSelector((state: RootState) => selectIsActive(state));
  const feedbackType = useSelector((state: RootState) =>
    selectFeedbackType(state),
  );
  const messageId = useSelector((state: RootState) => selectMessageId(state));
  const showForm = useSelector((state: RootState) => selectShowForm(state));
  const feedbackText = useSelector((state: RootState) =>
    selectFeedbackText(state),
  );
  const isSubmitted = useSelector((state: RootState) =>
    selectIsSubmitted(state),
  );
  const isSubmitting = useSelector((state: RootState) =>
    selectIsSubmitting(state),
  );
  const feedbackChatId = useSelector((state: RootState) =>
    selectFeedbackChatId(state),
  );

  // Action creators
  const startFeedback = useCallback(
    (messageId: string, feedbackType: FeedbackType, feedbackChatId: string) => {
      dispatch(
        startFeedbackAction({ messageId, feedbackType, feedbackChatId }),
      );
    },
    [dispatch],
  );

  const setFeedbackText = useCallback(
    (text: string) => {
      dispatch(setFeedbackTextAction(text));
    },
    [dispatch],
  );

  const startSubmitting = useCallback(() => {
    dispatch(startSubmittingAction());
  }, [dispatch]);

  const submitFeedbackSuccess = useCallback(() => {
    dispatch(submitFeedbackSuccessAction());
  }, [dispatch]);

  const submitFeedbackError = useCallback(() => {
    dispatch(submitFeedbackErrorAction());
  }, [dispatch]);

  const resetFeedback = useCallback(() => {
    dispatch(resetFeedbackAction());
  }, [dispatch]);

  return {
    // State
    isActive,
    feedbackType,
    messageId,
    showForm,
    feedbackText,
    isSubmitted,
    isSubmitting,
    feedbackChatId,
    // Actions
    startFeedback,
    setFeedbackText,
    startSubmitting,
    submitFeedbackSuccess,
    submitFeedbackError,
    resetFeedback,
  };
};
